/**
 * ============================================================================
 * SideButtons — Controller (client)
 * ============================================================================
 * Owns mobile detection, chatbot message listeners, and Call / WhatsApp
 * CTA handlers. Icons: v5 commonData.Section (CommonDataContext), see
 * resolveSideButtonItems. useCallback / useMemo keep the view stable across renders.
 * Behavior preserved from pre-MVC SideButtons.
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GAEvent, GA_EVENTS } from "@/helpers/gaEvents";
import { pushToDataLayer } from "@/helpers/gtag";
import { IModalActions, ModalTypes } from "@/types/modal";
import {
  getGlobalStateValue,
  sendPostMessage,
  updateStateSession,
} from "@/context/utils";
import { useModalDispatch } from "@/context/ModalContext";
import { useCommonData } from "@/context/CommonDataContext";
import { usePathname } from "next/navigation";
import { StorageService } from "@/helpers/storage.service";
import { FREEZE_RFIS } from "@/components/organisms/HeaderOpt/utils";
import { enableVideoControls } from "@/data/permissionUtils";
import {
  HERO_SCROLL_RATIO,
  SCHOLORSHIPS_PATHS,
  opensRfiForUrl,
  resolveSideButtonItems,
  resolveWhatsAppUrl,
  type SideButtonItem,
  type SideButtonsProps,
  type SideButtonsViewModel,
} from "../model/sideButtons.types";

export function useSideButtonsController(
  props: SideButtonsProps,
): SideButtonsViewModel {
  const {
    associatedPrograms = [],
    isTestScholarshipEnabled = false,
  } = props;

  const modalDispatch = useModalDispatch();
  const { sideButtons: sideButtonsV5 } = useCommonData();
  const { CALL_CHAT_INTERACTION } = GAEvent;
  const pathName = usePathname();
  const isBlogDetailsPage = pathName.includes("/blog");
  const filterTestScholorship =
    SCHOLORSHIPS_PATHS.includes(pathName) ||
    isBlogDetailsPage ||
    isTestScholarshipEnabled;

  const [isMobileView, setIsMobileView] = useState(false);
  const [isMobileViewDisabled, setIsMobileViewDisabled] = useState(true);
  // Hidden over the hero; shown once the user scrolls past it.
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);

  const associatedProgramsRef = useRef(associatedPrograms);
  associatedProgramsRef.current = associatedPrograms;

  const filterCallButton = useMemo(() => {
    const items = resolveSideButtonItems(sideButtonsV5);
    if (FREEZE_RFIS.includes(pathName)) {
      // RFI is frozen here, and blank / phone-url items open the RFI — drop those too.
      return items.filter(
        ({ title, url }) => title != "call-button" && !opensRfiForUrl(url),
      );
    }
    return items;
  }, [sideButtonsV5, pathName]);

  useEffect(() => {
    const handleChatbotMessage = (event: MessageEvent) => {
      if (event.data?.type === "CHATBOT_TOGGLE") {
        if (event.data.value) {
          if ((window as any)?.sideButtonsTimerCleanup) {
            (window as any).sideButtonsTimerCleanup();
            StorageService.set("chatbot-open", "true", "local");
          }
        }
      } else if (event.data?.type === "GET_DUPLICATE_SESSION_STORAGE_SEND") {
        sendPostMessage({
          type: "DUPLICATE_SESSION_STORAGE",
          data: JSON.parse(sessionStorage.getItem("globalStore") || "{}"),
        });
      }
    };
    window.addEventListener("message", handleChatbotMessage);
    return () => window.removeEventListener("message", handleChatbotMessage);
  }, []);

  // Hero sections have no shared marker across pages, so treat the first
  // ~70% of the viewport as the hero. rAF-throttled; re-checked per route.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      setIsVisible(window.scrollY > HERO_SCROLL_RATIO * window.innerHeight);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pathName]);

  useEffect(() => {
    if ((window as any)?.sideButtonsTimerCleanup) {
      (window as any).sideButtonsTimerCleanup();
    }

    StorageService.set("isTooltipPresent", filterTestScholorship);

    if (window.innerWidth < 1201) {
      setIsMobileView(true);
    } else {
      setIsMobileView(false);
    }
    setIsMobileViewDisabled(false);

    return () => {
      if ((window as any)?.sideButtonsTimerCleanup) {
        (window as any).sideButtonsTimerCleanup();
      }
      if ((window as any)?.commonModalTimerCleanup) {
        (window as any).commonModalTimerCleanup();
      }
      clearTimeout(timeoutRef.current);
      enableVideoControls();
    };
  }, [pathName]);

  const handleClick = useCallback(
    (item: SideButtonItem) => {
      const itemTitle = item?.title || "";
      const isCall = itemTitle === "call-button";
      const isWhatsApp = itemTitle === "whatsapp-link";
      if (!isCall && !isWhatsApp) return;
      if (isCall || isWhatsApp) {
        const gaData = {
          ...GA_EVENTS.get(CALL_CHAT_INTERACTION),
          cta_text: isCall ? "call_us" : "whatsapp_chat",
          section_name: "sticky_sidetray",
          page_type: getGlobalStateValue("pageType"),
        };

        setTimeout(() => {
          pushToDataLayer(gaData);
        }, 1500);

        if (isCall) {
          window?.webengage?.track("Call Us Clicked", {
            "Section Name": "sticky_sidetray",
          });
        }
      }
    },
    [CALL_CHAT_INTERACTION],
  );

  const renderItemUrl = useCallback(
    (itemUrl: string, title: string) =>
      resolveWhatsAppUrl(itemUrl, title, associatedProgramsRef.current),
    [],
  );

  const fireGtEvents = useCallback(() => {
    const programs = associatedProgramsRef.current;

    if (programs.length == 1) {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ConnectModalMba,
          modalData: {
            associatedPrograms: programs,
            callUsModal: true,
            isLandingPage: true,
            submitBtnText: "Apply Now",
            heading: "Join India's No. 1 Online University",
          },
        },
      });
    } else {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ConnectModalV2,
          modalData: {
            associatedPrograms: programs,
            callUsModal: true,
            isLandingPage: true,
            submitBtnText: "Apply Now",
            heading: "Join India's No. 1 Online University",
          },
        },
      });
    }

    setTimeout(() => {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "call_us",
        section_name: "sticky_sidetray",
        page_type: getGlobalStateValue("pageType"),
        page_url: window.location.href,
      });

      updateStateSession({
        parentSection: "sticky_sidetray",
        leadStartingCta: "call_us",
      });

      window?.webengage?.track("Call Us Clicked", {
        "Section Name": "sticky_sidetray",
      });
    }, 1500);
  }, [modalDispatch]);

  return {
    filterCallButton,
    isVisible,
    isMobileView,
    isMobileViewDisabled,
    fireGtEvents,
    handleClick,
    renderItemUrl,
  };
}
